import { useEffect, useState } from 'react';
import { PackagingService } from '../utility/services/packaging';
import { useDebouncedEffect } from './debounce';
import type {
  IPackagePallet,
  IPackageTote,
  IPalletContentItem,
  IToteContentItem,
} from 'types/fulfill/packing';
import { CommonUtility } from '@utility/index';

export const PackagingHook = warehouseId => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filter, setFilter] = useState({
    limit: 25,
    page: 1,
    search: '',
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const temp = {
        ...filter,
        warehouseId,
        search: (filter.search || '').trim(),
      };
      const res = await PackagingService.get(temp);
      setData(res?.data);
      setTotalData(res?.total);
      setTotalPages(res?.totalPages);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useDebouncedEffect(
    () => {
      if (warehouseId) {
        fetchData();
      }
    },
    [filter, refreshKey, warehouseId],
    500,
  );

  const pageChanged = page => {
    const temp = {
      ...filter,
      page,
    };
    setFilter({ ...temp });
  };

  const filterChanged = (key, value) => {
    const temp = {
      ...filter,
      page: 1,
      [key]: value,
    };
    setFilter({ ...temp });
  };

  const setSearch = search => {
    filterChanged('search', search);
  };

  const refresh = () => {
    setRefreshKey(Math.random());
  };

  const updateAllFilter = obj => {
    setFilter(filter => ({
      ...filter,
      page: 1,
      ...obj,
    }));
  };

  return {
    data,
    error,
    filter,
    filterChanged,
    loading,
    pageChanged,
    setSearch,
    totalData,
    totalPages,
    refresh,
    updateAllFilter,
  };
};

export const PackagingCompletedHook = warehouseId => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filter, setFilter] = useState({
    limit: 25,
    page: 1,
    search: '',
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const temp = {
        ...filter,
        warehouseId,
        search: (filter.search || '').trim(),
      };

      if (!temp.search) {
        delete temp.search;
      }

      const res = await PackagingService.getCompletedContainers(temp);
      setData(res?.data);
      setTotalData(res?.total);
      setTotalPages(res?.totalPages);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useDebouncedEffect(
    () => {
      if (warehouseId) {
        fetchData();
      }
    },
    [filter, refreshKey, warehouseId],
    500,
  );

  const pageChanged = page => {
    const temp = {
      ...filter,
      page,
    };
    setFilter({ ...temp });
  };

  const filterChanged = (key, value) => {
    const temp = {
      ...filter,
      page: 1,
      [key]: value,
    };
    setFilter({ ...temp });
  };

  const setSearch = search => {
    filterChanged('search', search);
  };

  const refresh = () => {
    setRefreshKey(Math.random());
  };

  const updateAllFilter = obj => {
    setFilter(filter => ({
      ...filter,
      page: 1,
      ...obj,
    }));
  };

  return {
    data,
    error,
    filter,
    filterChanged,
    loading,
    pageChanged,
    setSearch,
    totalData,
    totalPages,
    refresh,
    updateAllFilter,
  };
};

interface IPackingDetailHook {
  data: IContainer;
  loading: boolean;
  error: any;
  refresh: () => void;
  cancelledItems: (IPalletContentItem | IToteContentItem)[];
}

interface IContainer {
  tote?: IPackageTote;
  pallet?: IPackagePallet;
}

export const PackingDetailHook = (
  code: string,
  containerKey = 'code',
  status = null,
  warehouseId: string,
): IPackingDetailHook => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<IContainer>({});
  const [cancelledItems, setCancelledItems] = useState<
    (IPalletContentItem | IToteContentItem)[]
  >([]);
  const [error, setError] = useState(null);

  const fetchData = async (forceRefresh = false) => {
    setLoading(true);
    try {
      const params = {
        [containerKey]: code,
        warehouseId,
      };

      if (status) {
        params.status = status;
      }
      const res = (await PackagingService.getPackingContainerDetails(
        params,
        forceRefresh,
      )) as IContainer;

      const cancelledItems: (IPalletContentItem | IToteContentItem)[] = [];

      // @ts-expect-error string not assignable to type object
      if (CommonUtility.isValidObject(res.pallet)) {
        cancelledItems.push(...res.pallet.contents.filter(x => x.isCancelled));
        res.pallet.contents = res.pallet.contents.filter(x => !x.isCancelled);
        // @ts-expect-error string not assignable to type object
      } else if (CommonUtility.isValidObject(res.tote)) {
        cancelledItems.push(...res.tote.contents.filter(x => x.isCancelled));
        res.tote.contents = res.tote.contents.filter(x => !x.isCancelled);
      }
      setCancelledItems(cancelledItems);
      setData(res || {});
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (code && warehouseId) {
      fetchData();
    }
  }, [code, status, warehouseId]);

  const refresh = () => {
    fetchData(true);
  };

  return { data, loading, error, refresh, cancelledItems };
};

interface IPackageAutoCompleteHook {
  data: any[];
  error: any;
  loading: boolean;
  totalData: number;
  totalPages: number;
}

export const PackageAutoCompleteHook = (
  search: string,
  warehouseId: string,
): IPackageAutoCompleteHook => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params: { warehouseId: string; search?: string } = {
        warehouseId,
      };
      if (search) {
        params.search = search.trim();
      }
      const res = await PackagingService.get(params);
      setData(res?.data);
      setTotalData(res?.total);
      setTotalPages(res?.totalPages);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (warehouseId) {
      fetchData();
    }
  }, [search, warehouseId]);

  return {
    data,
    error,
    loading,
    totalData,
    totalPages,
  };
};
